import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react'
import {
  Box,
  Button,
  Grid,
  InputAdornment,
  Paper,
  TextField,
  Typography,
  Divider,
  FormControlLabel,
  Checkbox,
  List,
  ListItem
} from '@material-ui/core'
import PostCard from './components/PostCard'
import CreatePostModal from './components/CreatePostModal'
import ManageTopicsModal from './ManageTopicsModal'
import { ToggleButtonGroup, ToggleButton } from '@mui/material'
import { makeStyles } from '@material-ui/core/styles'
import { useDispatch, useSelector } from 'react-redux'
import AddIcon from '@material-ui/icons/Add'
import SearchIcon from '@material-ui/icons/Search'
import {
  listenToCommunityPosts,
  loadMoreCommunityPosts,
  setSearchQuery,
  fetchPostTopics
} from 'store/community'
import DataLoading from 'components/DataLoading'

const CommunityListing = () => {
  const classes = useStyles()
  const dispatch = useDispatch()

  const [filterType, setFilterType] = useState('all')
  const [selectedTopics, setSelectedTopics] = useState(['all'])
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [manageTopicsOpen, setManageTopicsOpen] = useState(false)
  const [loadingTopics, setLoadingTopics] = useState(false)

  const { conference } = useSelector((state) => state.conference)
  const { posts, postTopics, loadingState, searchQuery, hasMorePosts } = useSelector(
    (state) => state.community
  )

  const pageUnsubscribersRef = useRef([])
  const scrollContainerRef = useRef(null)
  const loadMoreInFlightRef = useRef(false)
  // Bumped every time the page-load effect (re)runs, so in-flight listenToCommunityPosts/
  // loadMoreCommunityPosts calls from a stale run can tell they're stale once their promise
  // resolves, even though cleanup already ran synchronously before that.
  const listenerGenerationRef = useRef(0)
  const [loadMoreNode, setLoadMoreNode] = useState(null)

  const topicIds = selectedTopics.includes('all') ? undefined : selectedTopics
  const filters = useMemo(() => ({ filterType, topicIds }), [filterType, topicIds])

  const cleanupPageListeners = () => {
    pageUnsubscribersRef.current.forEach((unsub) => typeof unsub === 'function' && unsub())
    pageUnsubscribersRef.current = []
    loadMoreInFlightRef.current = false
  }

  // Fetch posts when component mounts or conference changes
  useEffect(() => {
    const fetchTopics = async (conferenceId) => {
      setLoadingTopics(true)
      await dispatch(fetchPostTopics(conferenceId))
      setLoadingTopics(false)
    }
    if (conference?.id) {
      fetchTopics(conference?.id)
    }
  }, [dispatch, conference?.id])

  // (Re)load the first page whenever the conference or filters change
  useEffect(() => {
    if (!conference?.id) return undefined

    cleanupPageListeners()
    listenerGenerationRef.current += 1
    const generation = listenerGenerationRef.current

    Promise.resolve(dispatch(listenToCommunityPosts(conference.id, filters))).then((unsub) => {
      if (generation !== listenerGenerationRef.current) {
        typeof unsub === 'function' && unsub()
      } else {
        pageUnsubscribersRef.current[0] = unsub
      }
    })

    return cleanupPageListeners
  }, [dispatch, conference?.id, filters])

  const handleLoadMore = useCallback(() => {
    if (!conference?.id || !hasMorePosts || loadMoreInFlightRef.current) return

    const lastPost = posts[posts.length - 1]
    if (!lastPost) return

    const pageIndex = pageUnsubscribersRef.current.length
    const generation = listenerGenerationRef.current
    loadMoreInFlightRef.current = true

    Promise.resolve(dispatch(loadMoreCommunityPosts(conference.id, filters, pageIndex, lastPost)))
      .then((unsub) => {
        if (generation !== listenerGenerationRef.current) {
          typeof unsub === 'function' && unsub()
        } else {
          pageUnsubscribersRef.current[pageIndex] = unsub
        }
      })
      .finally(() => {
        loadMoreInFlightRef.current = false
      })
  }, [dispatch, conference?.id, filters, hasMorePosts, posts])

  // Infinite scroll: load the next page once the sentinel at the bottom of the list is visible.
  // loadMoreNode is set via a callback ref so the observer attaches as soon as the sentinel mounts
  // (it doesn't exist yet on first render, since the list is empty until posts load).
  useEffect(() => {
    if (!loadMoreNode) return undefined

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          handleLoadMore()
        }
      },
      { root: scrollContainerRef.current, rootMargin: '200px' }
    )

    observer.observe(loadMoreNode)
    return () => observer.disconnect()
  }, [loadMoreNode, handleLoadMore])

  // Handle search input change
  const handleSearchChange = (event) => {
    dispatch(setSearchQuery(event.target.value))
  }

  // Handle filter change
  const handleFilterChange = (event, newFilter) => {
    if (newFilter !== null) {
      setFilterType(newFilter)
    }
  }

  // Handle topic checkbox change
  const handleTopicChange = (topicId) => {
    if (topicId === 'all') {
      // If "All Topics" is clicked, reset to just 'all'
      setSelectedTopics(['all'])
    } else {
      // Remove 'all' if it's present when specific topics are selected
      const newSelectedTopics = selectedTopics.includes('all')
        ? [topicId]
        : selectedTopics.includes(topicId)
        ? selectedTopics.filter((id) => id !== topicId)
        : [...selectedTopics, topicId]

      // If all topics are deselected, default to 'all'
      setSelectedTopics(newSelectedTopics.length > 0 ? newSelectedTopics : ['all'])
    }
  }

  // Search only applies within posts already loaded on the client
  const orderedPosts = useMemo(() => {
    if (!searchQuery) return posts

    const query = searchQuery.toLowerCase()
    return posts.filter(
      (post) =>
        post?.title?.toLowerCase().includes(query) || post?.content?.toLowerCase().includes(query)
    )
  }, [posts, searchQuery])

  return (
    <Box>
      <Box className={classes.header}>
        <Typography variant="h4" className={classes.pageTitle}>
          Community Portal
        </Typography>
        <Box className={classes.headerActions}>
          <Box style={{ display: 'flex', gap: '10px' }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              className={classes.actionButton}
              onClick={() => setCreateModalOpen(true)}>
              Create Post
            </Button>
            <Button
              variant="outlined"
              color="primary"
              className={classes.actionButton}
              onClick={() => setManageTopicsOpen(true)}>
              Manage Topics
            </Button>
          </Box>
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={3}>
          <Paper className={classes.sidebar}>
            <Typography variant="subtitle1" className={classes.sidebarTitle}>
              Topics
            </Typography>
            <Divider />
            <List className={classes.topicList}>
              <ListItem className={classes.topicItem}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedTopics.includes('all')}
                      onChange={() => handleTopicChange('all')}
                      color="primary"
                    />
                  }
                  label="All Topics"
                />
              </ListItem>
              {loadingTopics ? (
                <DataLoading text="Loading topics..." progressSize={24} />
              ) : (
                postTopics?.map((topic) => (
                  <ListItem key={topic.id} className={classes.topicItem}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedTopics.includes(topic?.id)}
                          onChange={() => handleTopicChange(topic?.id)}
                          color="primary"
                        />
                      }
                      label={<Typography className={classes.topicLabel}>{topic?.title}</Typography>}
                    />
                  </ListItem>
                ))
              )}
            </List>
          </Paper>
        </Grid>

        <Grid item xs={12} md={9}>
          <Box className={classes.filterSection}>
            <ToggleButtonGroup
              value={filterType}
              exclusive
              onChange={handleFilterChange}
              aria-label="post filter"
              size="small"
              color="primary"
              className={classes.toggleGroup}>
              <ToggleButton value="all" aria-label="all posts">
                All Posts
              </ToggleButton>
              <ToggleButton value="pinned" aria-label="pinned posts">
                Pinned Posts
              </ToggleButton>
              <ToggleButton value="flagged" aria-label="flagged posts">
                Flagged Posts
              </ToggleButton>
            </ToggleButtonGroup>
            <TextField
              className={classes.searchField}
              size="medium"
              variant="outlined"
              fullWidth
              placeholder="Search by post title or content"
              value={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </Box>
          {loadingState.posts ? (
            <Box style={{ marginTop: '2rem' }}>
              <DataLoading />
            </Box>
          ) : orderedPosts.length > 0 ? (
            <Grid container spacing={3} className={classes.postGrid} ref={scrollContainerRef}>
              {orderedPosts?.map((post) => {
                return (
                  <Grid item xs={12} key={post?.id}>
                    <PostCard
                      post={post || {}}
                      postTopics={postTopics}
                      conferenceId={conference?.id}
                    />
                  </Grid>
                )
              })}
              {!searchQuery && (
                <Grid item xs={12} ref={setLoadMoreNode}>
                  {loadingState.moreposts && (
                    <Box style={{ display: 'flex', justifyContent: 'center', padding: 16 }}>
                      <DataLoading text="Loading more posts..." progressSize={24} />
                    </Box>
                  )}
                </Grid>
              )}
            </Grid>
          ) : (
            <Paper className={classes.emptyState}>
              <Typography variant="h6" gutterBottom>
                No posts found
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {searchQuery
                  ? 'No posts match your search criteria. Try a different search term.'
                  : 'Start by creating your first community post.'}
              </Typography>
              {!searchQuery && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={() => setCreateModalOpen(true)}
                  style={{ marginTop: 16 }}>
                  Create Post
                </Button>
              )}
            </Paper>
          )}
        </Grid>
      </Grid>

      <CreatePostModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        topics={postTopics}
        isEditing={false}
        postToEdit={null}
      />
      <ManageTopicsModal
        open={manageTopicsOpen}
        onClose={() => setManageTopicsOpen(false)}
        topics={postTopics}
        conferenceId={conference?.id}
        posts={posts}
      />
    </Box>
  )
}

export default CommunityListing

const useStyles = makeStyles((theme) => ({
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2)
  },
  pageTitle: {
    fontWeight: 600,
    fontSize: '22px'
  },
  headerActions: {
    // width: '60%',
    display: 'flex',
    gap: theme.spacing(2)
  },
  filterSection: {
    display: 'flex',
    justifyContent: 'space-between',
    // width: '100%',
    alignItems: 'center',
    flexWrap: 'wrap'
  },
  filterChips: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3)
  },
  chip: {
    cursor: 'pointer',
    fontWeight: 'normal'
  },
  activeChip: {
    cursor: 'pointer',
    fontWeight: 'bold'
  },
  flexSection: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2)
  },
  postGrid: {
    marginTop: '6px',
    maxHeight: 'calc(100vh - 300px)',
    overflow: 'auto'
  },
  toggleGroup: {
    '& .MuiToggleButton-root': {
      textTransform: 'none',
      fontWeight: 500,
      padding: '6px 16px',
      minWidth: '120px'
    },
    '& .MuiToggleButton-root.Mui-selected': {
      backgroundColor: theme.palette.primary.main,
      color: 'white',
      '&:hover': {
        backgroundColor: theme.palette.primary.main
      }
    }
  },
  menuButton: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    zIndex: 2
  },
  actionButton: {
    minWidth: '146px'
  },
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    borderRadius: theme.spacing(1.5),
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 4px 12px rgba(0,0,0,0.12)'
    }
  },
  cardContent: {
    flexGrow: 1,
    padding: theme.spacing(2)
  },
  pinIcon: {
    display: 'inline-block',
    transform: 'rotate(45deg)',
    color: theme.palette.secondary.main
  },
  pinnedIcon: {
    display: 'inline-block',
    transform: 'rotate(45deg)',
    position: 'absolute',
    top: '12px',
    right: '40px',
    color: theme.palette.secondary.main
  },
  searchField: {
    margin: '10px 0px',
    '& .MuiInputBase-root': {
      height: '46px'
    }

    // marginBottom: theme.spacing(3)
  },
  topic: {
    padding: '2px 8px',
    borderRadius: '16px',
    fontSize: '12px',
    backgroundColor: theme.palette.grey[100]
  },
  tagsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginTop: theme.spacing(1.5)
  },
  postStats: {
    display: 'flex',
    justifyContent: 'flex-start',
    marginTop: theme.spacing(2),
    color: theme.palette.text.secondary,
    gap: theme.spacing(3)
  },
  statItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5)
  },
  statCount: {
    fontSize: '0.875rem'
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(5)
  },
  cardActions: {
    justifyContent: 'flex-end',
    borderTop: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1)
  },

  authorSection: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    position: 'relative'
  },
  authorInfo: {
    marginLeft: theme.spacing(1.5),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center'
  },
  authorName: {
    fontWeight: 600,
    fontSize: '16px',
    lineHeight: 1.2
  },
  postTitle: {
    fontWeight: 600,
    fontSize: '18px',
    marginBottom: theme.spacing(1),
    lineHeight: 1.3
  },
  postContent: {
    color: theme.palette.text.secondary,
    lineHeight: 1.5,
    marginBottom: theme.spacing(2)
  },
  avatar: {
    width: 50,
    height: 50,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontSize: '1.2rem',
    fontWeight: 'bold',
    boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
  },
  postInteractions: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`
  },
  menuItem: {
    padding: theme.spacing(1, 2),
    display: 'flex',
    gap: theme.spacing(2)
  },
  listText: {
    fontSize: '14px',
    letterSpacing: '0.5px'
  },
  sidebar: {
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    height: 'auto',
    maxHeight: 'calc(100vh - 200px)', // Limit height with space for header
    // position: 'sticky',
    // top: '80px',
    overflowY: 'hidden' // Hide overflow initially
  },
  sidebarTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    color: theme.palette.primary.main
  },
  topicList: {
    padding: theme.spacing(1, 0),
    overflowY: 'auto', // Make list scrollable
    maxHeight: 'calc(100vh - 220px)', // Account for title and padding
    '&::-webkit-scrollbar': {
      width: '6px'
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.palette.grey[300],
      borderRadius: '3px'
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: theme.palette.grey[100]
    }
  },
  topicItem: {
    padding: theme.spacing(0.5, 0),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    '&:last-child': {
      marginBottom: theme.spacing(1) // Add space after the last item
    }
  },
  topicCount: {
    color: theme.palette.text.secondary,
    marginLeft: theme.spacing(1)
  },
  topicLabel: {
    margin: 0,
    '& .MuiTypography-root': {
      fontSize: '0.9rem'
    }
  }
}))
